<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';

// O que falta fazer no Google Cloud do cliente depois de criar a caixa, e o teste que diz se
// deu certo. A mesma peça na tela final da criação e na aba Configuração da caixa (GCHAT-01).
const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

// Cada `t()` com a chave inteira, e não montada a partir do código: chave montada na hora é
// invisível para o extrator de i18n (ver ChannelSection dos Power Agents).
const passos = computed(() => [
  t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.CHECKLIST.STEP_1'),
  t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.CHECKLIST.STEP_2'),
  t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.CHECKLIST.STEP_3'),
  t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.CHECKLIST.STEP_4'),
]);

const ERROS = {
  nao_configurado: () =>
    t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.TEST.ERRORS.NAO_CONFIGURADO'),
  sem_permissao: () =>
    t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.TEST.ERRORS.SEM_PERMISSAO'),
  chat_api_desligada: () =>
    t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.TEST.ERRORS.CHAT_API_DESLIGADA'),
  projeto_errado: () =>
    t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.TEST.ERRORS.PROJETO_ERRADO'),
};

const testando = ref(false);
// `{ ok: true }` ou `{ ok: false, codigo, detalhe }`, como o servidor devolve; `null` antes
// do primeiro teste.
const resultado = ref(null);

async function testar() {
  testando.value = true;
  resultado.value = null;
  try {
    const { data } = await InboxesAPI.testGoogleChat(props.inbox.id);
    resultado.value = data?.ok ? { ok: true } : { ok: false, ...data };
  } catch (e) {
    // O teste pode voltar com status de erro e o código no corpo: o código é o que importa.
    // Sem código, a falha é da chamada (rede, 5xx), não do Google, e diz isso.
    const corpo = e?.response?.data;
    resultado.value = corpo?.codigo
      ? { ok: false, ...corpo }
      : { ok: false, codigo: null };
  } finally {
    testando.value = false;
  }
}

const mensagem = computed(() => {
  const r = resultado.value;
  if (!r) return '';
  if (r.ok) return t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.TEST.OK');
  if (ERROS[r.codigo]) return ERROS[r.codigo]();
  if (r.codigo === 'erro_google' && r.detalhe) {
    return t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.TEST.ERRORS.ERRO_GOOGLE', {
      detalhe: r.detalhe,
    });
  }
  return t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.TEST.ERRORS.FALHA');
});

// "Não configurado" é a plataforma sem a credencial, não a caixa quebrada: aviso em âmbar,
// e não o vermelho dos erros de configuração do cliente (GCHAT-01, 2.1).
const tom = computed(() => {
  const r = resultado.value;
  if (!r) return null;
  if (r.ok) return { icone: 'i-lucide-circle-check', cor: 'text-n-teal-11' };
  if (r.codigo === 'nao_configurado') {
    return { icone: 'i-lucide-circle-alert', cor: 'text-n-amber-11' };
  }
  return { icone: 'i-lucide-circle-x', cor: 'text-n-ruby-11' };
});
</script>

<template>
  <div class="flex flex-col gap-5 text-left">
    <div class="flex flex-col gap-1.5">
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.API_CALLBACK.TITLE') }}
      </p>
      <woot-code lang="html" :script="inbox.callback_webhook_url || ''" />
    </div>

    <div class="flex flex-col gap-1.5">
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.CHECKLIST.TITLE') }}
      </p>
      <ol class="flex flex-col gap-1 mb-0 list-decimal ltr:pl-5 rtl:pr-5">
        <li
          v-for="passo in passos"
          :key="passo"
          class="text-sm text-n-slate-11"
        >
          {{ passo }}
        </li>
      </ol>
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <NextButton
        type="button"
        outline
        slate
        icon="i-lucide-plug-zap"
        :is-loading="testando"
        :label="t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.TEST.BUTTON')"
        @click="testar"
      />
      <p
        v-if="resultado"
        role="status"
        class="flex items-start flex-1 gap-1.5 mb-0 text-sm min-w-60"
        :class="tom.cor"
      >
        <i class="mt-0.5 text-base shrink-0" :class="tom.icone" />
        <span>
          {{ mensagem }}
          <code
            v-if="!resultado.ok && resultado.codigo"
            class="ltr:ml-1 rtl:mr-1 text-xs opacity-80"
          >
            {{ resultado.codigo }}
          </code>
        </span>
      </p>
    </div>
  </div>
</template>
